import {
  allLabel,
  isAllSelected,
  isSelected,
  type ChannelSelection,
  type FilterChannel,
} from '@agos/shared'

import { cn } from '../cn'

/**
 * §9.5's channel filter strip, shared by every board that shows orders.
 *
 * Multi-select with an all-channels tab, and the all tab is a tab like any other so a thumb can reach it
 * on a kitchen screen. The rule about what a tap means lives in `@agos/shared`'s `channel-filter`; this
 * draws it.
 *
 * Presentation only (§7): the owning page holds the selection and decides what to do with it.
 */
export function ChannelTabs({
  channels,
  selection,
  counts,
  onToggle,
  onAll,
  className,
}: {
  channels: readonly FilterChannel[]
  selection: ChannelSelection
  /** How many rows each channel has right now, so a tab says whether it is worth tapping. */
  counts?: Readonly<Record<string, number>>
  onToggle: (slug: string) => void
  onAll: () => void
  className?: string
}) {
  // One channel is not a choice, and a strip with a single tab beside *all* is noise on a busy screen.
  if (channels.length < 2) return null

  const total = Object.values(counts ?? {}).reduce((sum, count) => sum + count, 0)

  return (
    <div className={cn('flex flex-wrap gap-1.5', className)} data-channel-tabs={channels.length}>
      <button
        type="button"
        data-channel-tab="__all"
        data-channel-active={isAllSelected(selection) ? 'yes' : 'no'}
        onClick={onAll}
        className={cn(
          'min-h-[36px] px-3 rounded-xl text-xs font-bold border transition-colors',
          isAllSelected(selection)
            ? 'bg-primary text-primary-foreground border-primary'
            : 'bg-card border-border text-muted-foreground hover:text-foreground',
        )}
      >
        {allLabel()}
        {counts !== undefined && ` (${total})`}
      </button>

      {channels.map((channel) => {
        const active = !isAllSelected(selection) && isSelected(channel.slug, selection)
        const count = counts?.[channel.slug]
        return (
          <button
            key={channel.slug}
            type="button"
            data-channel-tab={channel.slug}
            data-channel-active={active ? 'yes' : 'no'}
            onClick={() => onToggle(channel.slug)}
            className={cn(
              'min-h-[36px] px-3 rounded-xl text-xs font-bold border transition-colors',
              active
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-card border-border text-muted-foreground hover:text-foreground',
              // An inactive channel still on the strip because its orders are here — dimmer, still usable.
              !channel.isActive && !active && 'opacity-60',
            )}
          >
            {channel.name}
            {count !== undefined && ` (${count})`}
          </button>
        )
      })}
    </div>
  )
}
